import { DayPicker } from 'react-day-picker'
import type { ComponentProps } from 'react'
import { cn } from '@/lib/utils'

type CalendarProps = ComponentProps<typeof DayPicker>

function Calendar({ className, classNames, numberOfMonths = 1, ...props }: CalendarProps) {
  return (
    <>
      <style>{calendarStyles}</style>
      <DayPicker
        numberOfMonths={numberOfMonths}
        className={cn('rdp-edge', className)}
        classNames={{
          months: 'rdp-edge-months',
          month: 'rdp-edge-month',
          month_caption: 'rdp-edge-caption',
          caption_label: 'text-xs font-medium',
          nav: 'rdp-edge-nav',
          button_previous: 'rdp-edge-nav-btn rdp-edge-nav-prev',
          button_next: 'rdp-edge-nav-btn rdp-edge-nav-next',
          month_grid: 'rdp-edge-grid',
          weekdays: 'rdp-edge-weekdays',
          weekday: 'rdp-edge-weekday',
          week: 'rdp-edge-week',
          day: 'rdp-edge-day',
          day_button: 'rdp-edge-day-btn',
          selected: 'rdp-edge-selected',
          today: 'rdp-edge-today',
          outside: 'rdp-edge-outside',
          disabled: 'rdp-edge-disabled',
          range_middle: 'rdp-edge-range-middle',
          range_start: 'rdp-edge-range-start',
          range_end: 'rdp-edge-range-end',
          hidden: 'invisible',
          ...classNames,
        }}
        {...props}
      />
    </>
  )
}

Calendar.displayName = 'Calendar'

export { Calendar }

const calendarStyles = `
  .rdp-edge {
    --rdp-accent-color: transparent;
    --rdp-accent-background-color: transparent;
    --rdp-range_start-color: inherit;
    --rdp-range_end-color: inherit;
    --rdp-range_start-background: transparent;
    --rdp-range_end-background: transparent;
    --rdp-range_start-date-background-color: transparent;
    --rdp-range_end-date-background-color: transparent;
    --rdp-range_middle-background-color: transparent;
    --rdp-range_middle-color: inherit;
    --rdp-day-height: auto;
    --rdp-day_button-height: auto;
    --rdp-nav-height: auto;
    --rdp-nav_button-height: auto;
    padding: 4px;
  }
  .rdp-edge table { border-collapse: collapse; }
  .rdp-edge-months {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
  }
  .rdp-edge-month { display: flex; flex-direction: column; gap: 4px; }
  .rdp-edge-caption {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 28px;
    position: relative;
  }
  .rdp-edge-nav {
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 28px;
    z-index: 1;
    pointer-events: none;
  }
  .rdp-edge-nav-btn {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    padding: 0;
    color: hsl(var(--muted-foreground));
    cursor: pointer;
    border-radius: 4px;
  }
  .rdp-edge-nav-btn:hover {
    color: hsl(var(--foreground));
    background: hsl(var(--accent));
  }
  .rdp-edge-nav-btn svg { width: 14px; height: 14px; }
  .rdp-edge-nav-prev { position: absolute; left: 0; top: 0; }
  .rdp-edge-nav-next { position: absolute; right: 0; top: 0; }
  .rdp-edge-grid { width: 100%; border-collapse: collapse; }
  .rdp-edge-weekdays { display: flex; }
  .rdp-edge-weekday {
    width: 32px;
    text-align: center;
    font-size: 10px;
    font-weight: 400;
    color: hsl(var(--muted-foreground));
  }
  .rdp-edge-week { display: flex; margin-top: 2px; }
  .rdp-edge-day { width: 32px; height: 28px; position: relative; padding: 0; text-align: center; font-size: 11px; }
  .rdp-edge-day-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 28px;
    padding: 0;
    font-size: 11px;
    font-weight: 400;
    border-radius: 6px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: inherit;
  }
  .rdp-edge-day-btn:hover {
    background: hsl(var(--accent));
    color: hsl(var(--accent-foreground));
  }
  .rdp-edge-selected .rdp-edge-day-btn {
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
  }
  .rdp-edge-selected .rdp-edge-day-btn:hover {
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
  }
  .rdp-edge-today .rdp-edge-day-btn {
    background: hsl(var(--primary) / 0.2);
    color: hsl(var(--primary));
    font-weight: 600;
  }
  .rdp-edge-outside { opacity: 0.3; }
  .rdp-edge-disabled { opacity: 0.25; pointer-events: none; }
  .rdp-edge-range-middle .rdp-edge-day-btn {
    background: hsl(var(--primary) / 0.15);
    border-radius: 0;
  }
  .rdp-edge-range-start .rdp-edge-day-btn { border-radius: 6px 0 0 6px; }
  .rdp-edge-range-end .rdp-edge-day-btn { border-radius: 0 6px 6px 0; }
  .rdp-edge-range-start.rdp-edge-range-end .rdp-edge-day-btn { border-radius: 6px; }
`
